import type { Dayjs } from "dayjs";
import type { ITimeProvider } from "../hexagon/adapters/driven/providers/provider";
import { NotesTableTypes } from "../hexagon/adapters/driven/enums";
import dayjs from "dayjs";
import { PickerValue } from "antd-mobile/es/components/picker-view";

// TODO use this constant throughout the app, and make a constant for production as well.
// Search "cipstaging.circadia.link" in the codebase to find all if/else cascades that use string literals that need to use constants.
export const STAGING_URLS = [
  "cipstaging.circadia.link",
  "cip-beta.circadia.link",
  "cipbeta.circadia.link",
];

export const LOCALHOST_URLS = ["localhost", "127.0.0.1"]; //, "0.0.0.0"];   // Allow using 0.0.0.0 to test a prod-like experience on a local machine.
export const NGROK_PATTERN = "ngrok.io";

// Reads a query param from either the real query string or the hash fragment.
// The app uses a hash router (e.g. /#/settings?showtools=false), so params are
// often after the `#` and therefore absent from `window.location.search`.
export const getQueryParam = (name: string): string | null => {
  const fromSearch = new URLSearchParams(window.location.search).get(name);
  if (fromSearch !== null) return fromSearch;

  const hash = window.location.hash;
  const queryIndex = hash.indexOf("?");
  if (queryIndex === -1) return null;
  return new URLSearchParams(hash.slice(queryIndex + 1)).get(name);
};

// True when developer/QA-only surfaces (e.g. the user's email) should be shown.
// An explicit ?showtools=true|false override wins over hostname detection in any
// environment, including production.
export const shouldShowDevTools = (): boolean => {
  const showTools = getQueryParam("showtools");
  if (showTools === "true") return true;
  if (showTools === "false") return false;

  return (
    STAGING_URLS.includes(window.location.hostname) ||
    LOCALHOST_URLS.includes(window.location.hostname) ||
    window.location.hostname.includes(NGROK_PATTERN)
  );
};

export const shouldShowMaskToggle = (): boolean => {
  // The mask toggle only has any effect on staging and localhost (see
  // `maskResidentName`), so it should only appear there — never in production
  // (not even when dev tools are force-opened via ?showtools=true) nor on ngrok
  // where masking is a no-op. An explicit ?showtools=false still hides it
  // everywhere.
  if (getQueryParam("showtools") === "false") return false;

  return (
    STAGING_URLS.includes(window.location.hostname) ||
    LOCALHOST_URLS.includes(window.location.hostname)
  );
};

export const extractNameFromEmail = (email: string): string => {
  const atIndex = email.indexOf("@");
  const name = email.substring(0, atIndex);
  return name.charAt(0).toUpperCase() + name.slice(1);
};

// ---------------------------------------------------------------------------
// CIRCADIA SSO PRIVILEGE-ESCALATION BUG -- spot-fix 2026-05-07
//
// Why the `!startsWith("pcc_")` guard exists:
//
// PCC SSO users (PointClickCare clinicians authenticating from outside the
// company) don't necessarily have a real email address. At some point a
// developer working on auth thought "users without an email need an email,
// I'll synthesize one" and started assigning them fictitious addresses of
// the shape `pcc_<something>@circadia.health`.
//
// Independently, other developers across the codebase wrote checks of the
// form `email.endsWith("@circadia.health")` assuming that domain is owned
// by the company and therefore anyone with such an email must be a Circadia
// employee.
//
// PCC SSO is not the bug. Each individual decision was reasonable in
// isolation. The bug is emergent: combining "synthesize fake emails on our
// own domain for users who don't have one" with "treat the corporate domain
// suffix as a proxy for employment" silently grants every external PCC SSO
// user every privilege we gate on `@circadia.health`.
//
// THE PROPER FIX is to mint these synthetic addresses on a separate,
// unprivileged domain so the privileged employee domain stays disjoint
// from any user-facing one, and then delete every `startsWith("pcc_")`
// patch like this one. Facebook did exactly this circa 2011: employees
// were issued `@fb.com` addresses (Facebook bought fb.com in 2010 for
// ~$8.5M precisely so the employee domain could be short and disjoint),
// while end-user Messenger/profile addresses lived on `@facebook.com`.
// Our equivalent would be something like `pcc_*@pcc-sso.circadia.invalid`
// (or any non-corporate domain we control) for the synthesized PCC
// addresses, leaving `@circadia.health` to mean "employee" again.
//
// Until that refactor lands, this is patched in TWO places only:
//   1. hasProjectNeoAccess              (cip-app/src/util/subdomain.ts)
//   2. shouldShowNpSchedulingSidebarLink (cip-app/src/util/common.ts)
//
// All of these other call sites STILL treat any `@circadia.health` email
// as internal/privileged and remain VULNERABLE to PCC SSO users:
//   - cip-app/src/util/devicelessRouting.ts
//       Auto-rewrites inbox/flag/alert API endpoints to their `-deviceless`
//       variants for clinics with no device installed.
//   - cip-app/src/pages/Settings/FacilityInformation/index.tsx
//       Enables editing the Facility Information textarea.
//   - cip-app/src/pages/VitalSync/useHook.tsx
//       Suppresses Mixpanel analytics events ("test user" filter).
//   - Patient-Centric-App-V2/app/crud/mysql/Alerts.py
//       Sets `flag_ack_by_is_internal = 1` on PSN flag acknowledgements.
//   - Patient-Centric-App-V2/app/crud/mysql/PSNV2_Flags.py
//       Excludes the user from the top-acknowledgers leaderboard.
//   - Patient-Centric-App-V2/app/controller/User.py
//       Excludes the user from external admission-team / general-user
//       email recipient splits.
//   - Patient-Centric-App-V2/app/controller/CronJobs/weekly_concise_summary_email.py
//   - Patient-Centric-App-V2/app/controller/CronJobs/weekly_concise_summary_email_v2.py
//       Exclude the user from external alerts / daily-report recipient lists.
// ---------------------------------------------------------------------------
export const shouldShowNpSchedulingSidebarLink = (
  user?: { isNursePractitioner?: boolean; email?: string } | null,
  betaFeaturesAllowedList?: readonly string[]
): boolean => {
  const email = user?.email?.toLowerCase() ?? "";
  const isInternalNp = Boolean(
    user?.isNursePractitioner &&
      email.endsWith("@circadia.health") &&
      !email.startsWith("pcc_") &&
      !email.includes("demo")
  );
  // Non-NP managers/supervisors don't carry the `isNursePractitioner`
  // flag, so the NP-only path above never shows them the Scheduling
  // entry point. The `np-supervisor` beta feature is an explicit
  // per-user grant in the DB (not an inferred signal like the Cognito-
  // derived NP flag), so we trust it directly — mirroring
  // `useNpSchedulingPermissions` in the NP Scheduling app, which gates
  // the in-app Admin tab on the same feature without an email guard.
  const isNpSupervisor = Boolean(
    betaFeaturesAllowedList?.includes("np-supervisor")
  );
  return isInternalNp || isNpSupervisor;
};

export enum SelectedDateFilter {
  ALL = 0,
  TODAY = 1,
  YESTERDAY = 2,
  LAST_WEEK = 3,
  LAST_MONTH = 4,
  CUSTOM_RANGE = 5,
  LAST_30_DAYS = 6,
}

export const getPriorityFromScore = (score: number) => {
  if (Math.trunc(score * 100) > 75) return "High";
  return "Moderate";
};

export const SelectedFrequency = {
  DAY_TIME: {
    value: 0,
    label: "Day Time",
    time: "7:00 a.m. - 3:00 p.m.",
    startTime: "07:00:00",
    endTime: "15:00:00",
    enum: "DAY_TIME" as frequencyType,
  },
  AFTERNOON: {
    value: 1,
    label: "Afternoon",
    time: "3:00 p.m. - 11:00 p.m.",
    startTime: "15:00:00",
    endTime: "23:00:00",
    enum: "AFTERNOON" as frequencyType,
  },
  NOCTURNAL: {
    value: 2,
    label: "Nocturnal",
    time: "11:00 p.m. - 7:00 a.m.",
    startTime: "23:00:00",
    endTime: "07:00:00",
    enum: "NOCTURNAL" as frequencyType,
  },
  ALL_DAY: {
    value: 3,
    label: "All Day",
    time: "Active 24 hours",
    startTime: "00:00:00",
    endTime: "23:59:59",
    enum: "ALL_DAY" as frequencyType,
  },
  CUSTOM: {
    value: 4,
    label: "Custom",
    time: "",
    startTime: "00:00:00",
    endTime: "23:59:59",
    enum: "CUSTOM" as frequencyType,
  },
};

export enum SelectedNoteFilter {
  CLINICAL_NOTES = "clinical_notes",
  ORDER_NOTES = "order_notes",
  CALL_DOCUMENT = "call_document",
}

export interface IDates {
  start?: Dayjs;
  end?: Dayjs;
}

export const dateFilterData = ({
  filterType,
  timeProvider,
  customDate,
}: {
  filterType: SelectedDateFilter;
  timeProvider: ITimeProvider;
  customDate?: IDates | null;
}) => {
  switch (filterType) {
    case SelectedDateFilter.ALL:
      return {
        // start of day of minus one month
        start: timeProvider.getDateTime().subtract(1, "month").startOf("day"),
        // end of day of today
        end: timeProvider.getDateTime().endOf("day"),
      };
    case SelectedDateFilter.TODAY:
      return {
        // start of day of today
        start: timeProvider.getDateTime().startOf("day"),
        // end of day of today
        end: timeProvider.getDateTime().endOf("day"),
      };
    case SelectedDateFilter.YESTERDAY:
      return {
        // start of day of yesterday
        start: timeProvider.getDateTime().subtract(1, "day").startOf("day"),
        // end of day of yesterday
        end: timeProvider.getDateTime().subtract(1, "day").endOf("day"),
      };
    case SelectedDateFilter.LAST_WEEK:
      return {
        // start of day minus one week of today
        start: timeProvider.getDateTime().subtract(1, "week").startOf("day"),
        // end of day of today
        end: timeProvider.getDateTime().endOf("day"),
      };
    case SelectedDateFilter.LAST_MONTH:
      return {
        // start of day minus one month of today
        start: timeProvider
          .getDateTime()
          .subtract(1, "month")
          .startOf("month")
          .startOf("day"),
        // end of day of today
        end: timeProvider.getDateTime().endOf("day"),
      };

    case SelectedDateFilter.LAST_30_DAYS:
      return {
        // start of day minus one month of today
        start: timeProvider.getDateTime().subtract(30, "days").startOf("day"),
        // end of day of today
        end: timeProvider.getDateTime().endOf("day"),
      };

    case SelectedDateFilter.CUSTOM_RANGE:
      if (customDate?.start && customDate?.end)
        return {
          // start of day minus one month of today
          start: dayjs(customDate?.start),
          // end of day of today
          end: dayjs(customDate?.end),
        };
  }
};

export const getBase64FromUrl = async (
  url: string
): Promise<string | ArrayBuffer | null> => {
  const data = await fetch(url);
  const blob = await data.blob();

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => {
      const base64data = reader.result;
      resolve(base64data);
    };
  });
};

export const getBase64FromFile = async (file: File) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      const base64data = reader.result;
      resolve(base64data);
    };
  });
};

export const getFileName = (name: string) => {
  const strSpt = name.split(".");
  const ext = strSpt.length > 1 ? strSpt.pop() : "";
  const nameWithoutExt = strSpt.join(".");

  return {
    name: name,
    ext: ext || "",
    nameWithoutExt,
  };
};

export const formatAbsoluteValue = (value = 0, precision = true) => {
  let finalValue;
  const parsedValue = Number(value);
  if (parsedValue > 0) {
    finalValue = precision ? `+${parsedValue.toFixed(1)}` : `+${parsedValue}`;
  } else {
    finalValue = precision ? parsedValue.toFixed(1) : parsedValue;
  }
  return Number(finalValue) === 0 ? 0 : finalValue;
};

export const bedExitFormatValues = (
  item: IDailyReportBedExit,
  type: string
) => {
  let newValue;
  if (type === "change") {
    newValue =
      formatAbsoluteValue(item?.shift_3_exits_change, false) === 0 &&
      item?.shift_3_exits_current === 0
        ? "-"
        : `(${formatAbsoluteValue(item?.shift_3_exits_change, false)})`;
  }

  if (type === "value") {
    newValue =
      item?.shift_3_exits_current === 0 &&
      formatAbsoluteValue(item?.shift_3_exits_change, false) === 0
        ? "-"
        : item?.shift_3_exits_current;
  }
  return newValue;
};

export const getLastCommaFirstName = (firstName: string, lastName: string) => {
  return `${lastName}, ${firstName}`;
};

export const getLastCommaFirstNameFromFullName = (fullNames: string) => {
  const names = fullNames.split(" ");
  if (names.length === 1) return names[0];
  return `${names[names.length - 1]}, ${names.slice(0, -1).join(" ")}`;
};

// Accepts either "Last, First" or "First Last" input and strips any trailing MRN parenthetical,
// so the result carries no direct identifier other than the surname.
export const getLastNameFirstInitial = (fullName: string) => {
  const withoutMrn = (fullName || "").replace(/\s*\([^)]*\)\s*$/, "").trim();
  if (!withoutMrn) return "";

  let lastName: string;
  let firstName: string;

  if (withoutMrn.includes(",")) {
    const [last, ...rest] = withoutMrn.split(",");
    lastName = last.trim();
    firstName = rest.join(",").trim();
  } else {
    const parts = withoutMrn.split(/\s+/);
    lastName = parts[parts.length - 1];
    firstName = parts.slice(0, -1).join(" ");
  }

  const initial = firstName.charAt(0).toUpperCase();
  return initial ? `${lastName}, ${initial}.` : lastName;
};

export const getPatientNotesTitleByTableType = (type: string) => {
  return NotesTableTypes[type as keyof typeof NotesTableTypes];
};

export const byteToMegaByte = (size: number) => {
  return size / 1024 / 1024;
};

export function toTitleCase(str: string) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

// Human-friendly EHR lab report status label. Case-insensitive, and special-cases
// "struckout" -> "Struck Out" to match the Labs table filter labels. Shared by
// the Labs table and the record-detail drawer so both stay in sync.
export function formatLabReportStatus(
  status: string | null | undefined
): string {
  if (!status) return "";
  const normalized = String(status).trim().toLowerCase();
  if (!normalized) return "";
  if (normalized === "struckout") return "Struck Out";
  return toTitleCase(normalized);
}

export function toCamelCase(str: string, separator: string): string {
  return str
    .toLowerCase()
    .split(separator)
    .map((part, index) =>
      index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)
    )
    .join("");
}

export const convert12HourTo24Hour = (
  time: PickerValue[],
  timeProvider: ITimeProvider
) => {
  const [hh, mm, A] = time;
  const hour = (Number(hh) % 12) + (A === "PM" ? 12 : 0);
  return timeProvider
    .dateLibrary()
    .set("hour", hour)
    .set("minute", Number(mm))
    .format("HH:mm:00");
};

export const convert24HourTo12Hour = (
  time: string,
  timeProvider: ITimeProvider
) => {
  const [hour, minute] = time.split(":");
  return timeProvider
    .dateLibrary()
    .set("hour", Number(hour))
    .set("minute", Number(minute))
    .format("hh:mm A");
};

export const arraysContainSameItems = (
  arr1: Array<number | string>,
  arr2: Array<number | string>
) => {
  const copyArr1 = arr1.slice();
  const copyArr2 = arr2.slice();

  copyArr1.sort();
  copyArr2.sort();

  if (copyArr1.length !== copyArr2.length) {
    return false;
  }

  for (let i = 0; i < copyArr1.length; i++) {
    if (copyArr1[i] !== copyArr2[i]) {
      return false;
    }
  }

  return true;
};

export const collatorForSorting = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

export const currenyFormater = (
  value: number,
  style: "decimal" | "currency" | "percent" | "unit" = "currency",
  fractionDigits = 0
) =>
  new Intl.NumberFormat("en-US", {
    style,
    currency: "USD",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);

export const getNewRecordsByNumberOfDays = <T>(
  arrayOfObject: T[] = [],
  noOfDaysBack: number = 2,
  columnToCompare: keyof T
) => {
  const endDate = dayjs().format("YYYY-MM-DD");
  const startDate = dayjs().subtract(noOfDaysBack, "days").format("YYYY-MM-DD");

  return arrayOfObject.filter((item) =>
    isDateBetween(String(item[columnToCompare]), startDate, endDate)
  );
};

// Write docstring for function below
/**
 * This function checks if the date is between the start and end date.
 * @param date - The date to check
 * @param startDate - The start date
 * @param endDate - The end date
 * @returns boolean
 */
export const isDateBetween = (
  date: string,
  startDate: string,
  endDate: string
) => {
  return dayjs(date)
    .startOf("day")
    .isBetween(
      dayjs(startDate).startOf("day"),
      dayjs(endDate).startOf("day"),
      null,
      "[]"
    );
};

// True when patient-name masking ("John Doe") is currently in effect: the
// hostname is a masking env (staging/localhost) AND the user hasn't turned
// the setting off (absent key defaults to ON). This is the single source of
// truth for both the local display transform (`maskResidentName`) and the
// `mask_pii: ["name"]` directive sent to Neo, so the chat and the rest of
// the app always agree on whether names are masked.
export const isNameMaskingActive = (): boolean => {
  const isMaskingEnv =
    STAGING_URLS.includes(window.location.hostname) ||
    LOCALHOST_URLS.includes(window.location.hostname);
  const maskEnabled = localStorage.getItem("maskPatientNames") !== "false"; // defaults to true if not set
  return isMaskingEnv && maskEnabled;
};

export const maskResidentName = (
  name: string,
  variant: "1" | "2" | "3" = "1"
): string => {
  if (isNameMaskingActive()) {
    if (variant === "1") return "Doe, John";
    if (variant === "2") return "John, Doe";
    if (variant === "3") return "John Doe";
  }

  return name;
};

export const getOperatingSystem = ({
  circadiaEngineProvider,
}: {
  circadiaEngineProvider: ICircadiaEngineProvider;
}) => {
  if (window.electron) {
    return "desktop";
  }

  if (circadiaEngineProvider.isWindowCircadia()) {
    return circadiaEngineProvider.isIOS ? "apple" : "android";
  }

  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes("win")) return "windows";

  // Fallback for other Apple devices
  if (userAgent.includes("like mac")) return "apple";

  if (
    userAgent.includes("mac") &&
    !userAgent.includes("iphone") &&
    !userAgent.includes("ipad") &&
    !userAgent.includes("ipod")
  )
    return "macintosh";

  // ChromeOS detection (check before linux since ChromeOS is linux-based)
  if (
    userAgent.includes("cros") ||
    userAgent.includes("chromeos") ||
    (userAgent.includes("chrome") &&
      userAgent.includes("linux") &&
      userAgent.includes("armv7l")) ||
    navigator.platform.toLowerCase().includes("cros") ||
    /\bCrOS\b/.test(navigator.userAgent)
  )
    return "chromeos";

  if (userAgent.includes("linux") && !userAgent.includes("android"))
    return "linux";
  if (userAgent.includes("android")) return "android";
  if (
    userAgent.includes("iphone") ||
    userAgent.includes("ipad") ||
    userAgent.includes("ipod")
  )
    return "apple";

  return "unknown";
};
